import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { components } from 'react-select'
import PropTypes from 'prop-types'
import Select from '../_form/Select'
import { getSelectedListViewOption, getListViewOptions } from '../../selectors/listViewSelectors'
import { setConditions } from '../../expressionbuilder/actions/expressionActions'
import { setColumns, setListView, setParam } from '../../actions/collectionActions'
import { fetchListViewsIfNeeded } from '../../actions/listViewActions'

const Heading = styled('h1')`
  font-size: 24px;
  margin-right: 1rem;
`

const styles = {
  container: (styles) => ({
    ...styles,
    minWidth: '400px',
    zIndex: 20,
  }),
  control: (styles) => ({
    ...styles,
    border: 'none',
    minWidth: '400px',
    boxShadow: 'none',
  }),
  menu: (styles) => ({
    ...styles,
    maxWidth: '400px',
  }),
  placeholder: (styles) => ({
    ...styles,
    color: '#212529',
    position: 'relative',
    transform: 'none',
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    position: isSelected ? 'relative' : styles.position,
    transform: isSelected ? 'none' : styles.transform,
  }),
  input: (styles, { isSelected }) => ({
    ...styles,
    position: isSelected ? 'relative' : styles.position,
    transform: isSelected ? 'none' : styles.transform,
  }),
  singleValue: (styles) => ({
    ...styles,
    position: 'relative',
    transform: 'none',
    maxWidth: 'auto',
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: 0,
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
}

const Placeholder = (props) => (
  <components.Placeholder {...props}>
    <Heading>{props.children}</Heading>
  </components.Placeholder>
)

const SingleValue = (props) => (
  <components.SingleValue {...props}>
    <Heading>{props.children}</Heading>
  </components.SingleValue>
)

const MenuList = (props) => <components.MenuList {...props}>{props.children}</components.MenuList>

const defaultSearch = { query: null, aggregate: true, keywords: null }

const ListViewSelect = ({ collection, placeholder, placeholderOnly, onChange }) => {
  const dispatch = useDispatch()

  const selected = useSelector((state) => getSelectedListViewOption(state, collection))
  const options = useSelector((state) => getListViewOptions(state, collection))

  useEffect(() => {
    dispatch(fetchListViewsIfNeeded())
  }, [])

  const handleChange = async (value) => {
    const search = value ? value.listView.searchQuery : defaultSearch
    const id = value ? value.listView.id : null

    // Only use StandardDataGrid format (v2) for employees collection
    const useStandardDataGridFormat = collection === 'employees'

    // Check if this is new format (v2 with MUI filter model) or old format (ExpressionBuilder)
    if (useStandardDataGridFormat && search && search.version === 2) {
      // New format: Apply grid state to sessionStorage (employees only)
      const gridStateKey = `${collection}GridState`
      const columnVisibilityKey = `${collection}ColumnVisibility`

      try {
        // Save filter, sort, and pagination to sessionStorage
        const gridState = {
          filterModel: search.filterModel || { items: [], linkOperator: 'and' },
          sortModel: search.sortModel || [],
          paginationModel: search.paginationModel || { page: 0, pageSize: 25 }
        }
        sessionStorage.setItem(gridStateKey, JSON.stringify(gridState))

        // Save column visibility to localStorage
        // Only save if it's not an empty object (which would make all columns visible)
        if (search.columnVisibilityModel && Object.keys(search.columnVisibilityModel).length > 0) {
          localStorage.setItem(columnVisibilityKey, JSON.stringify(search.columnVisibilityModel))
        }

        // Set aggregate parameter
        if (search.aggregate !== undefined) {
          dispatch(setParam(collection, 'aggregate', search.aggregate))
        }
      } catch (error) {
        // Silently fail - grid will use defaults
      }

      // Notify parent component with a special marker to reload the grid
      await onChange({ reloadGrid: true, aggregate: search.aggregate })
    } else if (useStandardDataGridFormat && !value) {
      // User clicked X to clear the saved list view for employees
      const gridStateKey = `${collection}GridState`

      try {
        // Clear filters and sort, but keep pagination
        const gridState = {
          filterModel: { items: [], linkOperator: 'and' },
          sortModel: [],
          paginationModel: { page: 0, pageSize: 25 }
        }
        sessionStorage.setItem(gridStateKey, JSON.stringify(gridState))
      } catch (error) {
        // Silently fail - grid will use defaults
      }

      // Notify parent component to reload the grid with cleared state
      await onChange({ reloadGrid: true, aggregate: true })
    } else {
      // Old format: Use existing logic for backwards compatibility and non-employees collections
      await onChange(search)
      dispatch(setColumns(collection, search.columns))
      dispatch(setParam(collection, 'direction', search.direction))
      dispatch(setParam(collection, 'sort', search.sort))
      dispatch(setConditions(collection, search.query))
    }

    // Mark this list view as selected
    dispatch(setListView(collection, id))
  }

  //  Only display text header when no saved searches are available
  if ((options && options.length === 0) || placeholderOnly) {
    return <Heading data-testid="list-view-select-heading">{placeholder}</Heading>
  }

  return (
    <Select
      components={{ MenuList, Placeholder, SingleValue }}
      isClearable
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      styles={styles}
      value={selected}
    />
  )
}

ListViewSelect.propTypes = {
  // Must return a Promise.
  onChange: PropTypes.func,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  placeholderOnly: PropTypes.bool,
  selected: PropTypes.object,
}

export default ListViewSelect
